import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { SKELETT_FARBEN, SKELETT_STUFEN, SKELETT_ZUSAETZE, SkelettStufe } from './skelett-stufen';

/**
 * Hilfe zum Skelettgehalt einer Substratlage. Vorher stand am (i) gar nichts —
 * die Spalte hatte keins.
 *
 * Die Spannen stehen in SEA 95, Teil B, Darst. B 3-15 (S. 73). Der Mittelwert
 * daneben ist das, womit die App rechnet: Er wird als Prozentsatz vom
 * Wasserspeicher abgezogen. Er steht nicht in der SEA, deshalb trägt er hier
 * seine eigene Quelle.
 */
@Component({
	standalone: true,
	imports: [CommonModule],
	template: `
		<div class="modal-header">
			<h5 class="modal-title">Skelettgehalt — Stein und Kies</h5>
			<button type="button" class="btn-close" aria-label="Schließen" (click)="activeModal.dismiss()"></button>
		</div>
		<div class="modal-body">
			<p class="einleitung">
				Der Skelettgehalt ist eine Zusatzbezeichnung zum Feinboden. Stein und Kies teilen
				sich dieselbe Stufenreihe; welches von beidem vorliegt, sagt das Kurzzeichen.
			</p>
			<p class="basis">
				Angegeben ist der Anteil an der Lage in <strong>Volumen-%</strong> — nicht in
				Masse-% wie beim Humusgehalt.
			</p>

			<table class="table table-sm skelett-tabelle">
				<thead>
					<tr>
						<th class="eingabe-spalte">Eingabe</th>
						<th class="code-spalte">Stein</th>
						<th class="code-spalte">Kies</th>
						<th class="spanne-spalte">Volumen-%</th>
						<th>Bezeichnung</th>
						<th class="abzug-spalte">Abzug</th>
					</tr>
				</thead>
				<tbody>
					<tr *ngFor="let s of stufen">
						<td class="eingabe-spalte">
							<span class="farbfeld" [style.background]="farbe(s)"></span>
							<span class="wert">{{ s.wert }}</span>
						</td>
						<td class="code-spalte"><code>{{ s.stein }}</code></td>
						<td class="code-spalte"><code>{{ s.kies }}</code></td>
						<td class="spanne-spalte">{{ s.spanne }}</td>
						<td>{{ s.bezeichnung }}</td>
						<td class="abzug-spalte">{{ abzug(s) }}</td>
					</tr>
				</tbody>
			</table>

			<div class="block">
				<h6>Was die Stufe bewirkt</h6>
				<p class="mb-0">
					Skelett speichert kein Wasser. Die Spalte „Abzug" ist der Mittelwert der
					Spanne; um diesen Anteil wird der nutzbare Wasserspeicher der Lage gekürzt.
					Eine mäßig steinige Lage (Stufe 4) verliert also gut ein Sechstel.
				</p>
			</div>

			<div class="block">
				<h6>Weitere Kurzzeichen</h6>
				<table class="table table-sm zusatz-tabelle mb-0">
					<tbody>
						<tr *ngFor="let z of zusaetze">
							<td class="code-spalte"><code>{{ z.code }}</code></td>
							<td>{{ z.bedeutung }}</td>
						</tr>
					</tbody>
				</table>
			</div>

			<p class="hinweis">
				Die Einteilung weicht bei den Stufen 0 bis 3 von der Kartieranleitung 1995 der
				geologischen Landesämter ab; die Stufen 4 bis 7 sind gleich. Damit haben auch
				die Kurzzeichen dort andere Inhalte.
			</p>

			<p class="quelle">
				Quelle: SEA 95, Teil B, Darst. B 3-15, S. 73. Schätzhilfen zum Vergleich im
				Gelände: Darst. B 3-17. Die Mittelwerte der Spalte „Abzug" stammen aus der
				nWSK-Rechnung nach Konopatzky 1998.
			</p>
		</div>
	`,
	styles: [`
		.modal-body {
			padding: 1rem 1.5rem;
		}
		.einleitung {
			margin-bottom: 0.5rem;
		}
		.basis {
			font-size: 0.88rem;
			color: #495057;
			margin-bottom: 1rem;
		}
		.skelett-tabelle {
			margin-bottom: 1rem;
		}
		.skelett-tabelle th {
			background: #f8f9fa;
			font-weight: 600;
			white-space: nowrap;
		}
		.skelett-tabelle td,
		.skelett-tabelle th {
			vertical-align: middle;
		}
		.eingabe-spalte {
			width: 6rem;
			white-space: nowrap;
		}
		.farbfeld {
			display: inline-block;
			width: 1.1rem;
			height: 1.1rem;
			border: 1px solid #adb5bd;
			border-radius: 3px;
			vertical-align: middle;
			margin-right: 0.5rem;
		}
		.wert {
			font-weight: 600;
			font-size: 1rem;
		}
		.code-spalte {
			width: 4.5rem;
			white-space: nowrap;
		}
		.skelett-tabelle code,
		.zusatz-tabelle code {
			font-size: 0.95rem;
			color: #212529;
			background: #f1f3f5;
			border-radius: 3px;
			padding: 0.1rem 0.4rem;
		}
		.spanne-spalte {
			width: 7rem;
			white-space: nowrap;
			text-align: right;
			padding-right: 1.25rem;
		}
		.abzug-spalte {
			width: 5rem;
			white-space: nowrap;
			text-align: right;
			color: #495057;
		}
		.block {
			border: 1px solid #dee2e6;
			border-radius: 4px;
			padding: 0.75rem 1rem;
			margin-bottom: 1rem;
			background: #f8f9fa;
		}
		.block h6 {
			font-weight: 600;
			margin-bottom: 0.5rem;
		}
		.block p {
			font-size: 0.9rem;
		}
		.zusatz-tabelle {
			background: #fff;
			font-size: 0.88rem;
		}
		.hinweis {
			font-size: 0.85rem;
			color: #495057;
			margin-bottom: 0.75rem;
		}
		.quelle {
			font-size: 0.82rem;
			color: #6c757d;
			margin-bottom: 0;
		}
	`]
})
export class SkelettgehaltInfoModalComponent {
	activeModal = inject(NgbActiveModal);

	stufen = SKELETT_STUFEN;
	zusaetze = SKELETT_ZUSAETZE;

	/**
	 * Von hell nach dunkel wie die Steindichte — nur zur Orientierung. Dieselbe
	 * Reihe färbt das Feld in der Substrattabelle (SKELETT_FARBEN in skelett-stufen.ts).
	 */
	farbe(s: SkelettStufe): string {
		return SKELETT_FARBEN[s.wert] ?? '#f8f9fa';
	}

	/** „—" statt „0 %": Bei skelettfrei wird nichts abgezogen. */
	abzug(s: SkelettStufe): string {
		if (!s.mittel) return '—';
		return `${String(s.mittel).replace('.', ',')} %`;
	}
}
